/**
 * EnergyControlReportHero — Powersure premium dark hero.
 *
 * Visuele lijn (Feb 2026 — concept owner):
 *  - Charcoal/donkergroene basis met bright emerald accenten
 *  - Korte uitleg, top 3 kansen, prijsblok, primaire CTA
 *  - "Apparaat koppelen" teaser als secundaire kernwaarde
 *  - Witte typografie, lucide-react iconen
 *  - Eén Powersure-card-systeem voor consistente energy-app sfeer
 */
import {
  ArrowDownToLine,
  Zap,
  Sparkles,
  Plug,
  CheckCircle2,
  ArrowRight,
  Home,
} from 'lucide-react';
import EnergyControlCheckoutButton from '@/components/fase1/EnergyControlCheckoutButton';

const TOP_KANSEN = [
  {
    icon: Sparkles,
    title: 'Eigen stroom slimmer gebruiken',
    body: 'Minder terugleveren, meer zelf benutten.',
  },
  {
    icon: Plug,
    title: 'Apparaten koppelen',
    body: 'P1, omvormer, laadpaal of warmtepomp in één overzicht.',
  },
  {
    icon: CheckCircle2,
    title: 'Beste volgende stap zien',
    body: 'Elke maand een concreet advies, geen ruis.',
  },
];

export default function EnergyControlReportHero({ intakeId, preview, fullReport, onScrollToReport }) {
  const opportunitiesCount = Array.isArray(preview?.opportunities) ? preview.opportunities.length : 0;
  const blocksCount = Array.isArray(fullReport?.blocks) ? fullReport.blocks.length : 0;
  const kansenSignal = opportunitiesCount >= 1 ? opportunitiesCount : (blocksCount >= 3 ? blocksCount : 0);

  const microLabel = kansenSignal >= 2
    ? `Uw woning heeft ${kansenSignal} slimme bespaarkansen`
    : 'Uw woning heeft meerdere slimme bespaarkansen';

  return (
    <section
      className="container mx-auto px-4 sm:px-6 pt-6"
      data-testid="ec-report-hero"
    >
      <div
        className="max-w-2xl mx-auto rounded-3xl border border-white/10 p-5 sm:p-7 shadow-2xl overflow-hidden relative"
        style={{
          // Dark teal-charcoal met subtiele emerald glow rechtsboven
          background:
            'radial-gradient(120% 80% at 100% 0%, rgba(16,185,129,0.18) 0%, rgba(16,185,129,0) 55%), linear-gradient(180deg, #0e1b16 0%, #0a1410 100%)',
        }}
      >
        {/* Brand mark */}
        <div className="flex items-center gap-2 mb-3 relative">
          <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
            <Zap className="w-4 h-4" />
          </span>
          <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-300/90 font-semibold">
            Powersure Energy Control
          </span>
        </div>

        <h1
          className="text-2xl sm:text-3xl font-medium text-white leading-tight"
          style={{ fontFamily: 'Outfit' }}
          data-testid="ec-report-hero-title"
        >
          Meer grip op uw zonnestroom
        </h1>
        <p className="text-sm sm:text-[15px] text-white/70 mt-2 leading-relaxed">
          In één oogopslag zien wat uw woning slimmer kan en welke stap nu het meeste oplevert
          — zonder ingewikkelde dashboards.
        </p>

        {/* Micro-personalisatie — rustige inline regel, geen euro-claim */}
        <div
          className="mt-3 inline-flex items-center gap-2 text-[12.5px] text-emerald-300/90"
          data-testid="ec-report-hero-micro"
        >
          <Home className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
          <span className="leading-snug">{microLabel}</span>
        </div>

        {/* Top 3 kansen */}
        <ul className="mt-5 space-y-2.5" data-testid="ec-report-hero-top3">
          {TOP_KANSEN.map(({ icon: Icon, title, body }, idx) => (
            <li
              key={idx}
              className="flex items-start gap-3 rounded-xl bg-white/5 border border-white/10 px-3.5 py-2.5 backdrop-blur-sm"
              data-testid={`ec-report-hero-kans-${idx + 1}`}
            >
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 shrink-0">
                <Icon className="w-4 h-4" />
              </span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-white leading-tight">{title}</div>
                <div className="text-xs text-white/65 mt-0.5 leading-snug">{body}</div>
              </div>
            </li>
          ))}
        </ul>

        {/* Apparaat koppelen teaser — kernwaarde, niet drukke kaart */}
        <div
          className="mt-4 flex items-center gap-3 rounded-xl bg-emerald-500/5 border border-emerald-400/20 px-3.5 py-2.5"
          data-testid="ec-report-hero-device-teaser"
        >
          <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-500/20 text-emerald-300 border border-emerald-400/30 shrink-0">
            <Plug className="w-4 h-4" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-medium text-white leading-tight">
              Koppel uw eerste apparaat in 1 minuut
            </div>
            <div className="text-[11.5px] text-white/55 mt-0.5">
              P1-meter, omvormer of laadpaal — direct na activatie.
            </div>
          </div>
          <ArrowRight className="w-4 h-4 text-emerald-300 shrink-0" />
        </div>

        {/* Prijsblok — direct duidelijk, premium dark */}
        <div
          className="mt-5 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3"
          data-testid="ec-report-hero-price"
        >
          <div className="text-[10.5px] uppercase tracking-[0.18em] font-semibold text-emerald-300/90">
            Abonnement
          </div>
          <div className="flex items-baseline gap-1.5 mt-1">
            <span
              className="text-3xl font-semibold text-white"
              style={{ fontFamily: 'Outfit' }}
              data-testid="ec-report-hero-price-amount"
            >
              € 9,95
            </span>
            <span className="text-sm text-white/80">per maand</span>
          </div>
          <div className="text-xs text-white/70 mt-1">
            Eerste 3 maanden, daarna <strong className="text-white">€ 14,95 per maand</strong> · maandelijks opzegbaar
          </div>
        </div>

        {/* Primaire + secundaire CTA */}
        <div className="mt-5 flex flex-col gap-2.5">
          <div className="w-full" data-testid="ec-report-hero-cta-wrap">
            <EnergyControlCheckoutButton
              enabled
              email={null}
              intakeId={intakeId}
              refCode={null}
              label="Start Powersure Energy Control"
              className="w-full py-3 text-[15px]"
            />
          </div>
          <button
            type="button"
            onClick={onScrollToReport}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md border border-white/15 bg-white/5 text-white/90 text-sm font-medium hover:bg-white/10 transition-colors"
            data-testid="ec-report-hero-secondary-cta"
          >
            Bekijk mijn volledige rapport
            <ArrowDownToLine className="w-4 h-4" />
          </button>
        </div>

        <p className="text-[11px] text-white/45 mt-3 leading-relaxed">
          Veilig betalen · geen leverancierswissel · geen automatische
          sturing zonder uw goedkeuring.
        </p>
      </div>
    </section>
  );
}
